import type { CounterProduct, Uuid } from '@/api/types';
import { CACHE, store } from './store';

/**
 * The snapshot the till runs on when it cannot reach the server.
 *
 * Taken on every successful load and refreshed on reconnect. Two things live
 * here, and they are the two the counter cannot invent for itself: where the
 * bill series had got to, and what was on the shelf.
 */

const KEY = 'counter-bootstrap';

export type CounterSnapshot = {
  pharmacyId: Uuid;
  capturedAt: string;
  billing: { prefix: string; financialYear: string; lastNumber: number };
  shelf: CounterProduct[];
  /**
   * How many bills this device has issued since the snapshot.
   *
   * The offline number is `lastNumber + issued + 1`. Stored rather than derived
   * from the outbox so that a bill which has already synced and left the queue
   * still counts — deriving it from the queue would re-issue numbers the moment
   * the queue drained.
   */
  issued: number;
  /**
   * Units taken off the shelf locally and not yet reflected in `shelf`, keyed
   * `medicineId:batchId`. What stops the till selling the same box twice while
   * it is cut off.
   */
  consumed: Record<string, number>;
};

export const counterCache = {
  async read(pharmacyId: Uuid): Promise<CounterSnapshot | null> {
    const snapshot = await store.get<CounterSnapshot>(CACHE, KEY);
    // A snapshot from another branch is worse than none: it would price and
    // number against a shop this device is no longer standing in.
    return snapshot?.pharmacyId === pharmacyId ? snapshot : null;
  },

  async write(snapshot: CounterSnapshot): Promise<void> {
    await store.put(CACHE, snapshot, KEY);
  },

  /**
   * Replaces the shelf and the series from a fresh server read.
   *
   * `issued` and `consumed` reset because the server's numbers now account for
   * everything this device has sent — anything still queued is counted again
   * by the caller before this is trusted.
   */
  async refresh(
    pharmacyId: Uuid,
    data: { capturedAt: string; billing: CounterSnapshot['billing']; shelf: CounterProduct[] },
    keep: { issued: number; consumed: Record<string, number> } = { issued: 0, consumed: {} },
  ): Promise<CounterSnapshot> {
    const snapshot: CounterSnapshot = { pharmacyId, ...data, ...keep };
    await counterCache.write(snapshot);
    return snapshot;
  },

  /**
   * Throws the cached shelf away. Deliberately does NOT touch the outbox.
   *
   * They look like one thing and are not: the snapshot is a copy of something
   * the server already knows and can always be taken again, while a queued bill
   * exists nowhere else in the world. Clearing both together would turn "the
   * stock figures look wrong" into lost sales.
   */
  async clear(pharmacyId: Uuid): Promise<void> {
    const snapshot = await store.get<CounterSnapshot>(CACHE, KEY);
    if (snapshot && snapshot.pharmacyId !== pharmacyId) return;
    await store.remove(CACHE, KEY);
  },
};

/**
 * The number this till will print next.
 *
 * Continues from the last number the server had issued when the snapshot was
 * taken, plus whatever this device has issued since — which is exactly the
 * scheme asked for, and it holds perfectly for one till.
 *
 * **The limit, stated plainly:** two tills that both go offline from the same
 * snapshot will both reach for the same number, and the second one to sync is
 * refused by the unique index on (pharmacy, bill number). That sale is not
 * lost — it lands in the outbox as a conflict for a human — but it does need a
 * human. A per-device prefix would remove the possibility entirely, and is the
 * thing to add the day a second counter appears.
 */
export function nextOfflineBillNumber(snapshot: CounterSnapshot): {
  billNumber: string;
  serialNumber: number;
} {
  const serialNumber = snapshot.billing.lastNumber + snapshot.issued + 1;
  const shortYear = snapshot.billing.financialYear.slice(2);
  return {
    billNumber: `${snapshot.billing.prefix}/${shortYear}/${String(serialNumber).padStart(4, '0')}`,
    serialNumber,
  };
}

const shelfKey = (medicineId: Uuid, batchId: Uuid) => `${medicineId}:${batchId}`;

/**
 * What a batch still has on it, after everything this device has sold offline.
 *
 * The guard against overselling while cut off. It is only as good as the
 * snapshot: an online order or a second till can still take stock this device
 * cannot see, which is why a sale is checked against the server again when it
 * syncs rather than trusted outright.
 */
export function availableInBatch(
  snapshot: CounterSnapshot,
  medicineId: Uuid,
  batchId: Uuid,
  onShelf: number,
): number {
  const taken = snapshot.consumed[shelfKey(medicineId, batchId)] ?? 0;
  return Math.max(0, onShelf - taken);
}

/** Records a sale against the local shelf and advances the local series. */
export function applyLocalSale(
  snapshot: CounterSnapshot,
  lines: { medicineId: Uuid; batchId: Uuid; units: number }[],
): CounterSnapshot {
  const consumed = { ...snapshot.consumed };
  for (const line of lines) {
    const key = shelfKey(line.medicineId, line.batchId);
    consumed[key] = (consumed[key] ?? 0) + line.units;
  }
  return { ...snapshot, issued: snapshot.issued + 1, consumed };
}
